import React, { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { SETTINGS_ROUTES } from "../../routes/routes";
import {
  getDialogPropDetails,
  initialScopedProps,
  isDialogOpen,
  setDialog,
} from "../../store/slices/global";
import theme from "../../theme";
import { contactSupport } from "../../utils/contactSupport";

interface ScopedDialogProps {}
const PREFIX = "ScopedDialogProps";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: "unset",
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: 15,
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

export const ScopedDialog: FC<ScopedDialogProps> = () => {
  const isOpen = useSelector(isDialogOpen("scoped"));
  const dialogProps = useSelector(getDialogPropDetails("scoped"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "scoped",
        open: false,
        props: initialScopedProps,
      })
    );
  };

  const defaultYesAction = () => {
    navigate(SETTINGS_ROUTES.Settings);
    dispatch(
      setDialog({ dialog: "scoped", open: false, props: initialScopedProps })
    );
  };

  const defaultNoAction = () => {
    contactSupport();
  };

  return (
    <StyledDialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={"xs"}
      PaperProps={{
        style: {
          display: !isOpen ? "none" : "flex",
          borderRadius: 15,
          width: "460px",
          height: "260px",
          justifyContent: "center",
          paddingBottom: "35px",
        },
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <Grid
          sx={{ paddingBottom: "0px" }}
          container
          direction={"row"}
          justifyContent={"flex-end"}
        >
          <IconButton onClick={handleClose}>
            <Close sx={{ color: "#B5B5B5" }} />
          </IconButton>
        </Grid>
        <DialogTitle id="dialogBox-title" className={classes.dialogTitle}>
          <Grid container item direction={"column"}>
            <Grid item>{dialogProps.title}</Grid>
          </Grid>
        </DialogTitle>

        <DialogContent sx={{ width: "346px" }}>
          <IgTypography>{dialogProps.message}</IgTypography>
        </DialogContent>
        {dialogProps.showButtons === false ? null : (
          <DialogActions>
            <IgButton
              onClick={() => {
                !dialogProps.noButtonAction
                  ? defaultNoAction()
                  : dialogProps.noButtonAction();
              }}
              sx={{
                letterSpacing: "0.5px",
                fontSize: "16px",
                fontWeight: 500,
                borderColor: theme.palette.disabled.main,
                color: theme.palette.gray.main,
              }}
              variant={"outlined"}
            >
              {dialogProps.noButtonText || "Contact Us"}
            </IgButton>
            <IgButton
              onClick={() => {
                !dialogProps.yesButtonAction
                  ? defaultYesAction()
                  : dialogProps.yesButtonAction();
              }}
              sx={{ letterSpacing: "0.5px", fontSize: "16px", fontWeight: 500 }}
              variant={"contained"}
            >
              {dialogProps.yesButtonText || "View Plans"}
            </IgButton>
          </DialogActions>
        )}
      </Grid>
    </StyledDialog>
  );
};
